/**
 * The HTML overlay above the 2D plan: dimension chips (click to edit inline)
 * and the add-segment menu. Pure projection of the editor state.
 *
 * `MeasureChips` is the chip rendering itself, over already-placed bars,
 * shared with the assembler's overlay. Both overlays take their bars from
 * core (`placedBars`, `placedAssemblyBars`), as the SVG bar lines do, so the
 * bars stay reachable while the view is zoomed in, each family shifting as
 * one unit with its chips.
 */

import { css } from '@emotion/react';
import type { EdgeSegmentType } from '@kaiizen/kai-floor-plan';
import {
  addEdgeItem,
  addMenuItemTypes,
  addMenuLayout,
  applyChipEdit,
  formatLength,
  isChipEditable,
  type PlacedBar,
  placedBars,
  useFloorPlanEditorState,
  useFloorPlanPreferences,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useRef } from 'react';
import { chipShadow, fMono, ink, lemon, white } from '../styles';
import { LengthInput } from './LengthInput';
import { OPTION_MENU_WIDTH, OptionMenu, optionMenuHeight } from './OptionMenu';

const DIM_CHIP_STYLE = css`
  label: DIM_CHIP_STYLE;
  position: absolute; transform: translate(-50%, -50%);
  background: ${white}; border: 1px solid ${ink}; color: ${ink};
  padding: 1px 6px; font-family: ${fMono}; font-size: 11px; font-weight: 700;
  letter-spacing: 0.02em;
  pointer-events: auto; cursor: text; white-space: nowrap;
  box-shadow: ${chipShadow};
  &:hover { background: ${lemon}; }
`;

/** Positions the shared feet-and-inches widget over the chip; mono like the chips. */
const DIM_INPUT_ANCHOR_STYLE = css`
  label: DIM_INPUT_ANCHOR_STYLE;
  position: absolute; transform: translate(-50%, -50%);
  pointer-events: auto;
  font-family: ${fMono}; font-size: 12px; font-weight: 700;
`;

function ChipInput({ initial, left, top, onCommit, onCancel }: {
  initial: number;
  left: number;
  top: number;
  onCommit: (value: number | null) => void;
  onCancel: () => void;
}): React.JSX.Element {
  // Guards the focusout that fires when the editor unmounts after commit/cancel.
  const finishedRef = useRef(false);
  return (
    <div css={DIM_INPUT_ANCHOR_STYLE} style={{ left, top }}>
      <LengthInput
        value={initial}
        autoFocus
        onCommit={v => {
          if (finishedRef.current) return;
          finishedRef.current = true;
          onCommit(v);
        }}
        onCancel={() => {
          if (finishedRef.current) return;
          finishedRef.current = true;
          onCancel();
        }}
      />
    </div>
  );
}

export interface MeasureChipsProps {
  placed: readonly PlacedBar[];
  /** The chip currently open for inline editing (its bar key), if any. */
  editingKey: string | null;
  /** Read-only: chips still display every measurement, but never open the inline editor. */
  readOnly?: boolean;
  /** Which chips may be edited (all, by default). */
  isEditable?: (key: string) => boolean;
  onBeginEdit: (key: string) => void;
  onCommit: (key: string, value: number | null) => void;
  onCancel: () => void;
}

/** The dimension chips over already-projected bars: click one to edit inline. */
export function MeasureChips(
  { placed, editingKey, readOnly = false, isEditable = () => true, onBeginEdit, onCommit, onCancel }: MeasureChipsProps,
): React.JSX.Element {
  const { units } = useFloorPlanPreferences();
  return (
    <>
      {placed.map(({ bar, g }) => {
        if (editingKey === bar.key) {
          return <ChipInput key={bar.key} initial={bar.value} left={g.cx} top={g.cy} onCommit={value => onCommit(bar.key, value)} onCancel={onCancel} />;
        }
        if (editingKey) return null;
        const editable = isChipEditable(bar.key, readOnly, isEditable);
        return (
          <div
            css={DIM_CHIP_STYLE}
            key={bar.key}
            data-testid='dim-chip'
            data-bar={bar.type}
            data-key={bar.key}
            style={editable ? { left: g.cx, top: g.cy } : { left: g.cx, top: g.cy, cursor: 'default' }}
            onClick={editable ? () => onBeginEdit(bar.key) : undefined}
          >
            {formatLength(bar.value, units)}
          </div>
        );
      })}
    </>
  );
}

export interface DimsOverlayProps {
  stageW: number;
  stageH: number;
}

export function DimsOverlay({ stageW, stageH }: DimsOverlayProps): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const { st } = floorPlanEditorState;
  const itemTypes = addMenuItemTypes(st);
  const menuLayout = addMenuLayout(st, { width: OPTION_MENU_WIDTH, height: optionMenuHeight(itemTypes.length) }, { width: stageW, height: stageH });

  return (
    <>
      <MeasureChips
        placed={placedBars(st, stageW, stageH)}
        editingKey={st.editingKey}
        readOnly={st.readOnly}
        onBeginEdit={key =>
          floorPlanEditorState.mutate(s => {
            s.editingKey = key;
          })}
        onCommit={(key, value) =>
          floorPlanEditorState.mutate(s => {
            s.editingKey = null;
            if (value != null) applyChipEdit(s, key, value, () => {});
          })}
        onCancel={() =>
          floorPlanEditorState.mutate(s => {
            s.editingKey = null;
          })}
      />
      {menuLayout && (
        <OptionMenu
          options={itemTypes as [string, string][]}
          onSelect={kind => floorPlanEditorState.mutate(s => addEdgeItem(s, kind as EdgeSegmentType))}
          style={{ position: 'absolute', ...menuLayout }}
          testId='add-menu'
        />
      )}
    </>
  );
}
